import { motion } from "framer-motion"
import { Link } from "wouter"
import { Video, Brain, Cloud, Scissors, Radio, Sparkles, Check } from "lucide-react"
import AtlasMark from "@/components/AtlasMark"
import FilmGrain from "@/components/FilmGrain"
import { SignInButton, SignUpButton, UserButton, useAuth } from "@clerk/clerk-react"

const features = [
  { icon: Brain, title: "Concept expansion", desc: "Turn one sentence into three distinct creative directions." },
  { icon: Video, title: "Shot planning", desc: "Every shot is editable, priced, and connected to the story." },
  { icon: Cloud, title: "Cloud rendering", desc: "GPU-powered generation with hard budget gates." },
  { icon: Scissors, title: "Master assembly", desc: "Validate, package, and download source-quality output." },
  { icon: Radio, title: "Publishing", desc: "Schedule and release directly to YouTube." },
]

const plans = [
  { name: "Free", price: "$0", desc: "Explore concepts and develop stories.", features: ["Unlimited concept expansion", "Story development", "Shot planning", "3 free preview renders"] },
  { name: "Pro", price: "$39", period: "/mo", desc: "For serious creators.", features: ["Everything in Free", "100 renders/month", "Cloud mastering", "YouTube publishing", "Priority GPU access"], highlight: true },
  { name: "Team", price: "$149", period: "/mo", desc: "For production teams.", features: ["Everything in Pro", "Unlimited renders", "Team collaboration", "Shared asset library", "Dedicated support"] },
]

export default function LandingPage() {
  const { isSignedIn } = useAuth()
  return (
    <div className="min-h-screen bg-[#08090d] text-[#f0f1f5]">
      <FilmGrain />
      <header className="fixed top-0 inset-x-0 z-50 h-16 px-5 sm:px-8 flex items-center justify-between border-b border-white/5 bg-[#08090d]/75 backdrop-blur-xl">
        <a href="/" className="flex items-center gap-2 text-sm font-semibold"><AtlasMark size={24} /> Atlas <span className="hidden sm:inline text-xs font-normal text-[#6b7280]">by HillPrime Innovations</span></a>
        <div className="flex items-center gap-2">
          {isSignedIn ? <><Link href="/studio"><a className="btn-subtle text-xs">Open Studio</a></Link><UserButton afterSignOutUrl="/" /></> : <><SignInButton mode="modal" fallbackRedirectUrl="/studio"><button className="btn-subtle text-xs">Sign in</button></SignInButton><SignUpButton mode="modal" fallbackRedirectUrl="/studio"><button className="btn-primary text-xs">Create account</button></SignUpButton></>}
        </div>
      </header>

      {/* Hero */}
      <section className="relative min-h-screen flex flex-col items-center justify-center px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#f59e0b]/5 via-transparent to-transparent pointer-events-none" />
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center max-w-3xl z-10"
        >
          <p className="eyebrow mb-4 text-[#f59e0b]">Atlas Studio</p>
          <h1 className="heading-display text-5xl sm:text-6xl md:text-7xl mb-6 text-balance">
            From first thought<br />
            <em className="text-[#f59e0b]">to final frame.</em>
          </h1>
          <p className="body-large mb-10 max-w-xl mx-auto text-balance">
            One guided production journey. Atlas helps shape the story, makes every decision visible, and never spends on rendering without your approval.
          </p>
          <Link href="/studio">
            <a className="btn-primary text-base px-8 py-3">
              Start creating <Sparkles className="w-4 h-4" />
            </a>
          </Link>
        </motion.div>
      </section>

      {/* Features */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="eyebrow mb-3 text-[#f59e0b]">What Atlas does</p>
            <h2 className="heading-serif text-3xl sm:text-4xl text-balance">Five stages. One clear path.</h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {features.map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="surface p-6 hover:border-[#2d3148] transition-colors group"
              >
                <f.icon className="w-6 h-6 text-[#f59e0b] mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="heading-sans text-base mb-2">{f.title}</h3>
                <p className="text-sm text-[#9ca3af]">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-24 px-6 border-t border-[#1e2130]">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="eyebrow mb-3 text-[#f59e0b]">Pricing</p>
            <h2 className="heading-serif text-3xl sm:text-4xl text-balance">Pay only for what you render.</h2>
            <p className="text-[#9ca3af] mt-3 max-w-lg mx-auto">Development, planning, and revising are free. Credit is spent only when a render you approved actually runs.</p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-4">
            {plans.map((plan, i) => (
              <motion.div
                key={plan.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className={`surface p-6 ${plan.highlight ? "border-[#f59e0b]/30 bg-[#f59e0b]/[0.02]" : ""}`}
              >
                <h3 className="heading-sans text-lg mb-1">{plan.name}</h3>
                <div className="flex items-baseline gap-1 mb-2">
                  <span className="text-3xl font-semibold text-[#f0f1f5]">{plan.price}</span>
                  {plan.period && <span className="text-sm text-[#6b7280]">{plan.period}</span>}
                </div>
                <p className="text-sm text-[#9ca3af] mb-6">{plan.desc}</p>
                <ul className="space-y-2.5 mb-6">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-[#9ca3af]">
                      <Check className="w-4 h-4 text-[#10b981] shrink-0 mt-0.5" />
                      {f}
                    </li>
                  ))}
                </ul>
                <Link href="/studio">
                  <a className={`block text-center w-full py-2.5 rounded-lg text-sm font-medium transition-colors ${plan.highlight ? "btn-primary" : "btn-subtle"}`}>
                    {plan.name === "Free" ? "Get started" : "Upgrade"}
                  </a>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="py-24 px-6 border-t border-[#1e2130]">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="heading-serif text-3xl sm:text-4xl mb-6 text-balance">Ready to make your next film?</h2>
          <Link href="/studio">
            <a className="btn-primary text-base px-8 py-3 inline-flex">Start creating <Sparkles className="w-4 h-4" /></a>
          </Link>
        </div>
      </section>

      <footer className="py-8 px-6 border-t border-[#1e2130] text-center text-xs text-[#6b7280]">
        Atlas by HillPrime Innovations · {new Date().getFullYear()}
      </footer>
    </div>
  )
}
